import React, { useState, useEffect, useRef } from "react";
import "./DebugLogger.css";

interface LogEntry {
  timestamp: string;
  type: "log" | "error" | "warn" | "info";
  message: string;
}

const DebugLogger: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logsRef = useRef<LogEntry[]>([]);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Function to add log entry
  const addLog = (type: LogEntry["type"], ...args: any[]) => {
    const message = args
      .map((arg) => {
        if (typeof arg === "object") {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(" ");

    const logEntry: LogEntry = {
      timestamp: new Date().toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
      }),
      type,
      message,
    };

    logsRef.current = [...logsRef.current.slice(-499), logEntry]; // Keep last 500 logs
    setLogs([...logsRef.current]);
  };

  useEffect(() => {
    // Store original console methods
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;

    // Override console methods
    console.log = (...args: any[]) => {
      originalLog.apply(console, args);
      addLog("log", ...args);
    };

    console.error = (...args: any[]) => {
      originalError.apply(console, args);
      addLog("error", ...args);
    };

    console.warn = (...args: any[]) => {
      originalWarn.apply(console, args);
      addLog("warn", ...args);
    };

    console.info = (...args: any[]) => {
      originalInfo.apply(console, args);
      addLog("info", ...args);
    };

    // Add initial log
    console.log("🐛 Debug Logger Initialized");
    console.log("📱 Platform:", window.navigator.platform);
    console.log("🌐 User Agent:", window.navigator.userAgent);
    console.log("🔊 Media Devices API available:", !!navigator.mediaDevices);

    // Cleanup on unmount
    return () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
      console.info = originalInfo;
    };
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new logs arrive
    if (logContainerRef.current && isOpen) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, isOpen]);

  const clearLogs = () => {
    logsRef.current = [];
    setLogs([]);
    console.log("🧹 Logs cleared");
  };

  const copyLogs = () => {
    const logText = logs
      .map((log) => `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`)
      .join("\n");

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(logText).then(
        () => {
          alert("Logs copied to clipboard!");
        },
        () => {
          // Fallback for older browsers
          fallbackCopyToClipboard(logText);
        }
      );
    } else {
      fallbackCopyToClipboard(logText);
    }
  };

  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      alert("Logs copied to clipboard!");
    } catch (err) {
      alert("Failed to copy logs. Please manually copy from the log window.");
    }

    document.body.removeChild(textArea);
  };

  const downloadLogs = () => {
    const logText = logs
      .map((log) => `[${log.timestamp}] [${log.type.toUpperCase()}] ${log.message}`)
      .join("\n");

    const blob = new Blob([logText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `debug-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getLogColor = (type: LogEntry["type"]) => {
    switch (type) {
      case "error":
        return "#ff4444";
      case "warn":
        return "#ffaa00";
      case "info":
        return "#4488ff";
      default:
        return "#333333";
    }
  };

  const getLogIcon = (type: LogEntry["type"]) => {
    switch (type) {
      case "error":
        return "❌";
      case "warn":
        return "⚠️";
      case "info":
        return "ℹ️";
      default:
        return "📝";
    }
  };

  return (
    <>
      {/* Bug Button - Absolute position inside chatbot */}
      <button
        className="debug-bug-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Debug Console"
        style={{
          position: "absolute",
          bottom: "80px",
          right: "20px",
          width: "50px",
          height: "50px",
          borderRadius: "50%",
          backgroundColor: isOpen ? "#ff4444" : "#91278f",
          color: "white",
          border: "none",
          fontSize: "24px",
          cursor: "pointer",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
        }}
      >
        {isOpen ? "✖" : "🐛"}
      </button>

      {/* Debug Console Modal */}
      {isOpen && (
        <div
          className="debug-logger-modal"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.95)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "10px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "100%",
              height: "95%",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "2px solid #91278f",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f5f5f5",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <div>
                <h3 style={{ margin: 0, color: "#91278f", fontSize: "20px" }}>
                  🐛 Debug Console
                </h3>
                <p style={{ margin: "4px 0 0", fontSize: "12px", color: "#666" }}>
                  Total Logs: {logs.length} | Platform: {window.navigator.platform}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#666",
                  padding: "0 8px",
                }}
              >
                ✖
              </button>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                padding: "12px 20px",
                display: "flex",
                gap: "10px",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#fafafa",
                flexWrap: "wrap",
              }}
            >
              <button
                onClick={clearLogs}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#ff4444",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                🗑️ Clear
              </button>
              <button
                onClick={copyLogs}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#4488ff",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                📋 Copy
              </button>
              <button
                onClick={downloadLogs}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#22aa44",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                💾 Download
              </button>
              <button
                onClick={() => {
                  console.log("🧪 Test Log");
                  console.warn("⚠️ Test Warning");
                  console.error("❌ Test Error");
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#91278f",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                🧪 Test
              </button>
              <button
                onClick={async () => {
                  console.log("🎤 Testing microphone access...");
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    console.log("✅ Microphone test PASSED - access granted");
                    console.log("✅ Stream tracks:", stream.getTracks().length);
                    stream.getTracks().forEach(track => {
                      console.log("✅ Track:", track.label, "enabled:", track.enabled);
                      track.stop();
                    });
                    console.log("✅ Microphone test complete - tracks stopped");
                  } catch (err: any) {
                    console.error("❌ Microphone test FAILED:", err.name, err.message);
                  }
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#ff8800",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                🎤 Test Mic
              </button>
            </div>

            {/* Logs Container */}
            <div
              ref={logContainerRef}
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "16px",
                backgroundColor: "#1e1e1e",
                fontFamily: "monospace",
                fontSize: "13px",
              }}
            >
              {logs.length === 0 ? (
                <div style={{ color: "#888", textAlign: "center", padding: "40px 20px" }}>
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>📭</div>
                  <div>No logs yet. Interact with the app to see logs here.</div>
                </div>
              ) : (
                logs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "8px",
                      padding: "8px 12px",
                      backgroundColor: log.type === "error" ? "#3a1a1a" : log.type === "warn" ? "#3a3a1a" : "#2a2a2a",
                      borderRadius: "4px",
                      borderLeft: `4px solid ${getLogColor(log.type)}`,
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    <div style={{ display: "flex", gap: "8px", marginBottom: "4px" }}>
                      <span style={{ color: "#888", fontSize: "11px" }}>
                        {log.timestamp}
                      </span>
                      <span style={{ fontSize: "14px" }}>{getLogIcon(log.type)}</span>
                      <span
                        style={{
                          color: getLogColor(log.type),
                          fontWeight: "bold",
                          fontSize: "11px",
                        }}
                      >
                        {log.type.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ color: "#e0e0e0", paddingLeft: "0px" }}>
                      {log.message}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DebugLogger;
